import React from 'react';
import { useSelector } from 'react-redux';
import { Provider, useAtomValue, useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';
import { globalFilterAtom } from '@src/atoms/page';
import { SummaryContainer } from '@components/summary-panel';
import { MetricsChartContainer as HzMetricsChartContainer } from '@components/metrics-panel-hz';
import { ErrorBoundary, PageTitle, Footer, Retry } from '@components/common';
import WidgetPanelContainer from '@components/top-widget-panel';
import { SAVE_SERVICE_DASHBOARD_KEYS } from '@utils/constants';
import { selector as commonSelector } from '@store/common';
import { getFirstAppliedFilter } from '@store/common/selectors';
import RetryContext from '@src/contexts/retryContext';
import DimensionContainer from '@components/dimension-panel-hz';
import MetricFilterBar from '@components/summary-panel/MetricFilterBar';
import AlertPanel from '@components/alert-panel';
import { buildDashboardStore } from '@src/atoms/utils';
import CompareFiltersBar from '@components/summary-panel/CompareFiltersBar';
import { ContentWrapper, SectionWrapper } from '../main/styled';
import { useSetError } from '@src/hooks/useSetError';
import { useResetForPreset } from '@src/hooks/useResetForPreset';
import { horizontalLayoutAtom } from '@src/components/metrics-panel-hz/atoms';
import ActionToolbar from '@src/components/ActionToolbar';
import usePageMeta from '@src/hooks/usePageMeta';
import ZenPanel from '@src/components/zen';
import { usePersistAtoms } from '@hooks';
import dataToPersistSelector from '@src/store/common/persistence';
import useApplyAtomPersistence from '@src/hooks/useApplyAtomPersistence';
import useSelfServeRedirect from '@src/hooks/eco/useSelfServeIntegrationStatus';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import { useFrustrationSignalConfigs } from '@src/hooks/frustrationSignal/useFrustrationSignalConfigs';
import FlowBreadcrumb from '@components/common/Breadcrumb/FlowBreadcrumb';
import FrustrationSignalBreadcrumb from '@components/common/Breadcrumb/FrustrationSignalBreadcrumb';
import EventBreadcrumb from '@components/common/Breadcrumb/EventBreadcrumb';
import PresetBreadcrumb from '@components/common/Breadcrumb/PresetBreadcrumb';
import { metricsStore } from '@src/globalStore/metricsStore';
import useKPI from '@src/hooks/useKPI';
import KPISettingsDrawer from '@components/kpi-settings-drawer';

const defaultStore = buildDashboardStore();

const AppExperience = (props) => {
  useSetError();
  // Initialize KPI within the scoped Jotai Provider
  useKPI();
  const { title = 'Trends', id, isSharing, isSavedDashboard }: any = props;
  const setGlobalFilter = useSetAtom(globalFilterAtom);
  const appliedFilter = useSelector(getFirstAppliedFilter);
  setGlobalFilter(appliedFilter);
  const horizontalLayout = useAtomValue(horizontalLayoutAtom);
  const { flowId, frustrationSignalId, eventId, presetId } = useDrilldownMeta();
  const { flowConfigMeta } = metricsStore.getState();
  const frustrationSignalConfigMeta = useFrustrationSignalConfigs();
  useResetForPreset();
  usePersistAtoms({ bindSelector: dataToPersistSelector });
  const { interval } = useSelector(commonSelector);

  // if the Self Serve feature flag is enabled, we need to check the integration status
  // we may redirect to the welcome page if the integration status is not set
  const { isFetched } = useSelfServeRedirect();

  if (!isFetched) {
    return <Retry isLoading />;
  }

  return (
    <RetryContext.Provider value={{ interval }}>
      <ErrorBoundary>
        {/* If this is a flow preset (flowId present) show breadcrumb: Overview > Flow Name */}
        {/* If this is a frustration signal preset (frustrationSignalId present) show breadcrumb: Overview > Frustration Signal Name */}
        {/* If this is a standard preset (presetId present) show breadcrumb: Overview > Preset Name */}
        {flowId ? (
          <FlowBreadcrumb
            flowId={flowId}
            flowConfigMeta={flowConfigMeta}
            title={title}
            isSharedSaved={isSharing || isSavedDashboard}
          />
        ) : frustrationSignalId ? (
          <FrustrationSignalBreadcrumb
            signalId={frustrationSignalId}
            signalConfigMeta={frustrationSignalConfigMeta}
            title={title}
            isSharedSaved={isSharing || isSavedDashboard}
          />
        ) : eventId ? (
          <EventBreadcrumb
            eventId={eventId}
            title={title}
            isSharedSaved={isSharing || isSavedDashboard}
          />
        ) : presetId ? (
          <PresetBreadcrumb
            presetId={presetId}
            title={title}
            isSharedSaved={isSharing || isSavedDashboard}
          />
        ) : null}
        <PageTitle text={title} />
        <SummaryContainer id={id} hasBreadcrumb={!!(flowId || frustrationSignalId || presetId)} />
        <CompareFiltersBar />
        <MetricFilterBar />
        <WidgetPanelContainer />
        <AlertPanel />
        <ContentWrapper horizontalLayout={horizontalLayout}>
          <ActionToolbar />
          <SectionWrapper horizontalLayout={horizontalLayout}>
            <HzMetricsChartContainer />
            <DimensionContainer />
            <ZenPanel />
          </SectionWrapper>
          <Footer />
        </ContentWrapper>
        <KPISettingsDrawer />
      </ErrorBoundary>
    </RetryContext.Provider>
  );
};

const WrappedAppExperience = (props) => {
  const { store = defaultStore }: any = props;
  const { id = SAVE_SERVICE_DASHBOARD_KEYS.APP_EXPERIENCE } = useParams();

  usePageMeta({ id, store });

  useApplyAtomPersistence({ pageProps: props, store });

  return (
    <Provider store={store}>
      <AppExperience {...props} id={id} />
    </Provider>
  );
};

export default WrappedAppExperience;
